import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Building2, Map, ChevronRight, Send, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export default function ChatDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const title = location.state?.title || "Chat";
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [tripLink, setTripLink] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    apiFetch("/chats")
      .then((chats) => {
        const found = chats.find((c) => c.id === id);
        if (found?.trip_id) setTripLink({ id: found.trip_id, label: found.context_label || "Viaggio collegato" });
      })
      .catch(() => {});
    apiFetch(`/chats/${id}/messages`).then(setMessages).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !id) return;
    setSending(true);
    try {
      const item = await apiFetch(`/chats/${id}/messages`, { method: "POST", body: JSON.stringify({ text: text.trim() }) });
      setMessages((old) => [...old, item]);
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-3rem)] max-w-3xl flex-col">
      <div className="flex items-center gap-3 border-b border-border pb-3">
        <button onClick={() => navigate(-1)} className="rounded-lg p-1.5 hover:bg-secondary">
          <ArrowLeft className="h-6 w-6 text-foreground" />
        </button>
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary">
          <Building2 className="h-5 w-5 text-primary" />
        </span>
        <p className="text-base font-extrabold text-foreground">{title}</p>
      </div>

      {tripLink ? (
        <Link to={`/trip/${tripLink.id}`} data-testid="chat-trip-link" className="mt-3 flex items-center gap-2 rounded-xl bg-secondary px-3 py-2.5 text-secondary-foreground">
          <Map className="h-4 w-4 text-primary" />
          <span className="flex-1 truncate text-[13px] font-extrabold">{tripLink.label}</span>
          <ChevronRight className="h-4 w-4 text-primary" />
        </Link>
      ) : null}

      <div className="flex-1 space-y-3 overflow-y-auto py-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          messages.map((m) => {
            const mine = user?.id && m.sender_id === user.id;
            return (
              <div key={m.id} className={cn("max-w-[82%] rounded-2xl p-3", mine ? "ml-auto bg-secondary" : "bg-card border border-border")}>
                <p className="text-[10px] font-bold text-muted-foreground">{m.sender_name}</p>
                <p className="mt-0.5 text-[15px] leading-snug text-foreground">{m.text}</p>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-2 border-t border-border py-3">
        <input
          data-testid="chat-message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Scrivi un messaggio..."
          className="min-h-[48px] flex-1 rounded-full border border-border bg-card px-4 text-[15px] outline-none focus:border-primary"
        />
        <button data-testid="chat-send-button" onClick={send} disabled={sending} className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-60">
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}
